import React, { Fragment, useState, useEffect } from "react";
import { ExpandMore, ExpandLess, MoreHoriz } from "@material-ui/icons";
import SubmissionSummaryChart from "./SubmissionSummaryChart";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getExpandedRowModel,
} from "@tanstack/react-table";
import TableLoadingSkeleton from "../Reports/TableLoadingSkeleton";
import StatusTag from "../StatusTag";
import { toSnakeCase } from "../utils/StringFormatters";
import ProgressBar from "../ProgressBar";
import ActionsMenu from "./ActionsMenu";
import generateReceiptPdf from "../Submissions/shared/generateReceiptPdf";

const columnHelper = createColumnHelper();

const LoadingIndicator = () => {
  return (
    <td className="loading">
      <div className="loading-indicator loading-indicator--blue loading-indicator--large" />
    </td>
  );
};

export default function SubmissionsTable({
  submissionType,
  submissions,
  api_submissions_row_data_path,
  api_submissions_summary_path,
  isLoading,
  currentPageIndex,
  setNotice,
}) {
  const [rowsLoading, setRowsLoading] = useState([]);

  const startRowLoading = (row) => setRowsLoading([...rowsLoading, row.original.guid]);
  const stopRowLoading = (row) =>
    setRowsLoading(rowsLoading.filter((guid) => guid !== row.original.guid));
  const isRowLoading = (row) => rowsLoading.includes(row.original.guid);

  const collapseAllRows = () => {
    for (const row of table.getRowModel().rows) {
      if (row.getIsExpanded()) {
        row.toggleExpanded();
      }
    }
  };

  useEffect(() => {
    collapseAllRows();
  }, [currentPageIndex]);

  const fetchRow = (row) => {
    startRowLoading(row);
    fetch(`${api_submissions_row_data_path}/${row.original.guid}`)
      .then((response) => {
        if (!response.ok) {
          return response.json().then((errorData) => {
            throw new Error(errorData.error);
          });
        }
        return response.json();
      })
      .then((data) => {
        row.original = { ...row.original, ...data };
      })
      .catch((error) => {
        row.toggleExpanded();
        setNotice({ kind: "error", open: true, message: "Error fetching data." });
      })
      .finally(() => stopRowLoading(row));
  };

  const toggleRow = (row) => {
    if (isRowLoading(row)) return;

    if (row.getIsExpanded()) {
      row.toggleExpanded();
    } else {
      row.toggleExpanded();
      fetchRow(row);
    }
  };

  const columns = [
    columnHelper.accessor("file_name", {
      header: "Submission",
      cell: ({ row, getValue }) => {
        function renderIcon() {
          if (row.getIsExpanded()) {
            return <ExpandLess fontSize="small" />;
          }
          return <ExpandMore fontSize="small" />;
        }
        return (
          <>
            <div
              className="btn btn--sqr"
              style={{ marginRight: 18, verticalAlign: "middle" }}
              onClick={() => toggleRow(row)}
            >
              {renderIcon()}
            </div>
            <span className="t--500 t--sm">{getValue()}</span>
          </>
        );
      },
    }),
    columnHelper.accessor((row) => row.status, {
      header: "Status",
      cell: ({ getValue }) => {
        return <StatusTag status={getValue()} type="submission" />;
      },
    }),
    columnHelper.accessor("claim_count", {
      header: "Claims",
      cell: (info) => {
        const isProcessing = toSnakeCase(info.row.original.status) === "processing";
        return <div className="t--sm">{isProcessing ? "--" : info.renderValue()}</div>;
      },
    }),
    columnHelper.accessor("conforming_percent", {
      header: "% Conforming",
      cell: ({ getValue }) => {
        return <ProgressBar color="blue" percent={getValue()} barLength={60} gap={8} />;
      },
    }),
    columnHelper.accessor("date", {
      header: "Date",
      cell: ({ getValue }) => <div className="t--sm">{getValue()}</div>,
    }),
    columnHelper.accessor("links", {
      header: "actions",
      cell: ({ getValue, row }) => {
        const link = getValue().download;
        const guid = row.original.guid;

        const handleDownloadReceipt = () => {
          const summaryUrl = `${api_submissions_summary_path}/${guid}/summary`;
          fetch(summaryUrl)
            .then((res) => {
              if (!res.ok) throw new Error("Failed to fetch summary");
              return res.json();
            })
            .then((data) => generateReceiptPdf(data))
            .catch(() =>
              setNotice({ kind: "error", open: true, message: "Error downloading receipt." })
            );
        };

        return (
          <>
            <a id={link} href={link}></a>
            <ActionsMenu
              options={[
                {
                  id: "download-claims",
                  label: "Download Claims",
                  action: () => document.getElementById(link).click(),
                },
                {
                  id: "download-receipt",
                  label: "Download Receipt",
                  action: handleDownloadReceipt,
                },
              ]}
            />
          </>
        );
      },
    }),
  ];

  const table = useReactTable({
    data: submissions,
    columns,
    getExpandedRowModel: getExpandedRowModel(),
    getCoreRowModel: getCoreRowModel(),
  });

  const renderSubComponent = (row) => {
    return (
      <SubmissionSummaryChart
        submissionType={submissionType}
        claimsData={row?.original?.claims_data}
      />
    );
  };

  const renderRows = () => {
    if (isLoading) return <TableLoadingSkeleton rows={20} />;
    return table.getRowModel().rows.map((row) => {
      return (
        <Fragment key={row.id}>
          <tr className="sm">
            {row.getVisibleCells().map((cell) => (
              <td key={cell.id} className="t--md">
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
          {row.getIsExpanded() && (
            <tr>
              {isRowLoading(row) ? (
                <LoadingIndicator />
              ) : (
                <td
                  className="submission_summary__container"
                  colSpan={row.getVisibleCells().length}
                >
                  {renderSubComponent(row)}
                </td>
              )}
            </tr>
          )}
        </Fragment>
      );
    });
  };

  return (
    <div>
      <table className="primary">
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="sm">
              {headerGroup.headers.map((header) => (
                <th key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>{renderRows()}</tbody>
      </table>
    </div>
  );
}
